import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/client'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'
import { Trans } from '@lingui/react/macro'
import clsx from 'clsx'

import Loading from '@/components/ui/Loading'

import OracleLogo from '@/assets/pms/OracleLogo.webp'
import MewsLogo from '@/assets/pms/MewsLogo.webp'
import ApaleoLogo from '@/assets/pms/ApaleoLogo.webp'
import StayNTouchLogo from '@/assets/pms/StayNTouchLogo.webp'

const logos = {
  [GraphQL.Providers.Oracle]: OracleLogo,
  [GraphQL.Providers.Mews]: MewsLogo,
  [GraphQL.Providers.Apaleo]: ApaleoLogo,
  [GraphQL.Providers.Stayntouch]: StayNTouchLogo
}

function Provider({
  name,
  loading,
  onClick
}: {
  name: GraphQL.Providers
  loading: boolean
  onClick: () => void
}) {
  return (
    <button
      className="relative border border-text-light rounded p-3 flex flex-col gap-2.5 items-center justify-end cursor-pointer aspect-square w-52"
      onClick={onClick}
      tabIndex={0}
    >
      {!loading && (
        <div className="absolute inset-0 size-full p-5">
          <img className="size-full object-contain" src={logos[name]} />
        </div>
      )}
      {loading && <Loading />}
      <h3 className="text-heading3 text-text">{name}</h3>
    </button>
  )
}

export function ProviderSelector() {
  const [startPms, { loading }] = useMutation(AdminAPI.pms.CREATE)
  const [selectedProvider, setProvider] = useState<GraphQL.Providers | null>(
    null
  )

  const onSelect = useCallback(
    (prov: GraphQL.Providers) => {
      const provider = prov

      setProvider(provider)

      startPms({
        variables: {
          provider
        },
        refetchQueries: [AdminAPI.pms.GET]
      })
    },
    [setProvider, startPms]
  )

  return (
    <div className="flex flex-col gap-2 p-2.5">
      <h2 className="text-heading2 text-text">
        <Trans>Choose your Provider</Trans>
      </h2>
      <div
        className={clsx(
          'flex flex-row flex-wrap gap-4 p-4',
          loading && 'pointer-events-none select-none'
        )}
      >
        {Object.values(GraphQL.Providers).map(provider => (
          <Provider
            key={provider}
            name={provider}
            loading={selectedProvider === provider ? loading : false}
            onClick={() => onSelect(provider)}
          />
        ))}
      </div>
      <p className="text-regular text-text">
        <Trans>
          If your Provider is missing, please write us an email to{' '}
          <a
            className="text-info-500 underline"
            href="mailto:help@guestgoodie.com"
          >
            help@guestgoodie.com
          </a>
          . If the Provider supports integrations, we can set one up for you.
        </Trans>
      </p>
    </div>
  )
}
